import type { ParsedUrlQuery, ParsedUrlQueryInput } from "querystring";

type RedirectQueryValue = ParsedUrlQuery[string] | ParsedUrlQueryInput[string];

type RedirectQuery =
  | ParsedUrlQuery
  | ParsedUrlQueryInput
  | Record<string, RedirectQueryValue>;

type RedirectQuerySource = RedirectQuery | URLSearchParams;

export const isTrackingParam = (key: string) =>
  key.startsWith("utm_") || key === "_gl";

const isRedirectQueryParam = (key: string) =>
  key === "next" || isTrackingParam(key);

const normalizeRedirectQueryValue = (
  value: RedirectQueryValue,
): ParsedUrlQueryInput[string] => {
  if (Array.isArray(value)) {
    return value.map(String);
  }

  return value;
};

const getQueryValues = (value: RedirectQueryValue | string): string[] => {
  if (value == null) return [];
  return Array.isArray(value) ? value.map(String) : [String(value)];
};

export const pickRedirectQueryParams = (
  query: RedirectQuery | undefined,
): ParsedUrlQueryInput => {
  const redirectQueryParams: ParsedUrlQueryInput = {};

  Object.entries(query || {}).forEach(([key, value]) => {
    if (!isRedirectQueryParam(key) || value == null) return;
    redirectQueryParams[key] = normalizeRedirectQueryValue(value);
  });

  return redirectQueryParams;
};

const appendQueryParamValue = (
  searchParams: URLSearchParams,
  key: string,
  value: RedirectQueryValue | string,
) => {
  getQueryValues(value).forEach((currentValue) => {
    searchParams.append(key, currentValue);
  });
};

const getBaseOrigin = () =>
  (typeof window !== "undefined" && window.location?.origin) ||
  "https://localhost";

export const appendRedirectQueryParams = (
  searchParams: URLSearchParams,
  query: RedirectQuerySource | undefined,
) => {
  if (!query) return searchParams;

  if (query instanceof URLSearchParams) {
    query.forEach((value, key) => {
      if (!isRedirectQueryParam(key)) return;
      searchParams.append(key, value);
    });
    return searchParams;
  }

  Object.entries(query).forEach(([key, value]) => {
    if (!isRedirectQueryParam(key) || value == null) return;
    appendQueryParamValue(searchParams, key, value);
  });

  return searchParams;
};

export const getRedirectQueryString = (
  query: RedirectQuerySource | undefined,
) => {
  const searchParams = new URLSearchParams();
  appendRedirectQueryParams(searchParams, query);
  return searchParams.toString();
};

export const stripRedirectQueryParams = (url: string): string => {
  if (!url) return url;
  const baseOrigin = getBaseOrigin();
  const parsedUrl = new URL(url, baseOrigin);

  [...parsedUrl.searchParams.keys()]
    .filter(isRedirectQueryParam)
    .forEach((key) => parsedUrl.searchParams.delete(key));

  const cleanSearch = parsedUrl.searchParams.toString();
  const searchSuffix = cleanSearch ? `?${cleanSearch}` : "";

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return `${parsedUrl.origin}${parsedUrl.pathname}${searchSuffix}${parsedUrl.hash}`;
  }

  return `${parsedUrl.pathname}${searchSuffix}${parsedUrl.hash}`;
};

export const appendTrackingParamsToUrl = (
  destination: string,
  query: RedirectQuery | undefined,
) => {
  const trackingEntries = Object.entries(query || {}).filter(([key]) =>
    isTrackingParam(key),
  );
  if (trackingEntries.length === 0) return destination;

  const destinationUrl = new URL(destination, getBaseOrigin());

  trackingEntries.forEach(([key, value]) => {
    const existingValues = new Set(destinationUrl.searchParams.getAll(key));

    getQueryValues(value).forEach((currentValue) => {
      if (existingValues.has(currentValue)) return;
      destinationUrl.searchParams.append(key, currentValue);
      existingValues.add(currentValue);
    });
  });

  if (destination.startsWith("http://") || destination.startsWith("https://")) {
    return destinationUrl.toString();
  }

  return `${destinationUrl.pathname}${destinationUrl.search}${destinationUrl.hash}`;
};
